/* eslint-disable @typescript-eslint/naming-convention */
import { pull } from 'lodash';
import { getCollisionIds, type IShape } from '@/utils/collision-kit';
import type { ILayerProps, IPoint } from '@/version-types';
import {
    getElementOffset,
    getMouseMoveRect,
    getPointer,
    isLayerProps,
    Point,
    sendPointToPlane,
    type IService,
    type TPointerEvent,
} from '../../shared';
import type { ServiceManager } from '../service-manager';

/**
 * 图层坐标变换服务
 */
export class TransformService implements IService {
    constructor(readonly service: ServiceManager) {}

    onInit() {}

    /**
     * 根据鼠标Y位置获取舞台索引
     *
     * @param identity - 选择器标识 ID
     * @param y - Y 坐标
     */
    getFrameIndexByMousePointY(y: number) {
        const { edgelessCanvas, frame } = this.service;
        if (!edgelessCanvas.isLongImageMode) {
            return frame.getActiveIndex();
        }
        const list = frame.getRenderFrameIds();
        for (let i = 0; i < list.length; i++) {
            const { top, height } = this.getFrameViewportRect(i);
            if (y >= top && y <= top + height) {
                return i;
            }
        }
        return 0;
    }

    /** 根据完整鼠标位置获取命中的画布索引 */
    getFrameIndexByMousePoint(point: IPoint) {
        const frameIndex = this.getFrameIndexByMousePointY(point.y);
        return this.checkPointInFrame(point, frameIndex) ? frameIndex : undefined;
    }

    /**
     * 计算点是否在事件层
     *
     * @param point - 坐标点
     */
    checkPointInViewport(point: IPoint) {
        const rect = this.service.editor.getBoundingClientRect();
        if (
            point.x >= rect.left &&
            point.y >= rect.top &&
            point.x <= rect.width + rect.left &&
            point.y <= rect.top + rect.height
        ) {
            return true;
        }
        return false;
    }

    /** 检查点是否在舞台内 */
    checkPointInFrame(point: IPoint, index: number) {
        if (this.service.edgelessCanvas.isEdgelessMode) {
            return this.checkPointInViewport(point);
        } else {
            const rect = this.getFrameViewportRect(index);
            if (
                point.x >= rect.left &&
                point.y >= rect.top &&
                point.x <= rect.width + rect.left &&
                point.y <= rect.top + rect.height
            ) {
                return this.checkPointInViewport(point);
            }
        }
        return false;
    }

    /**
     * 获取舞台视图大小
     * @param index - 画布索引
     */
    getFrameViewportRect(index: number) {
        const { edgelessCanvas, frame } = this.service;
        const scale = edgelessCanvas.zoom;
        const sizeList = frame.getSizeList(scale);
        const frameRect = frame.get(frame.indexToFrameId(index));
        const rect = this.service.editor.getBoundingClientRect();
        return {
            left: (frameRect?.left ?? 0) * scale + edgelessCanvas.translateX + rect.left,
            top: (frameRect?.top ?? 0) * scale + edgelessCanvas.translateY + rect.top,
            width: sizeList[index]?.width ?? 0,
            height: sizeList[index]?.height ?? 0,
        };
    }

    /**
     * 获取鼠标选择框
     *
     * @param startPoint - 起始点
     * @param endPoint - 结束点
     */
    getMouseMoveRect(startPoint: IPoint, endPoint: IPoint): IShape {
        return getMouseMoveRect(startPoint, endPoint);
    }

    /**
     * 获取一个点的选区
     * @param point - 坐标点
     * @returns
     */
    getPointRect(point: IPoint): IShape {
        return this.getMouseMoveRect(point, { x: point.x + 1, y: point.y + 1 });
    }

    /** 获取激活舞台的碰撞信息 */
    getShapes(stageIndex: number, deep: boolean): IShape[] {
        const originIndex = this.service.frame.indexToFrameId(stageIndex);
        const collisionLayers = this.service.frame.getCollisionLayers(originIndex);
        return this.convertToShapes(collisionLayers, deep);
    }

    /** 计算图层偏移量，用于长图模式下 */
    calcLayerOffsetTop(layer: ILayerProps | IShape) {
        if (isLayerProps(layer)) {
            return this.service.frame.getFrameTransform(layer.__originIndex || 0).offsetTop;
        }
        return 0;
    }

    /** 计算鼠标矩形偏移量，用于长图模式下 */
    private __calcMouseRectOffsetTop(shape: IShape) {
        return { ...shape, top: shape.top + this.calcLayerOffsetTop(shape) };
    }

    /** 转换为形状 */
    convertToShapes(layers: ILayerProps[], deep: boolean): IShape[] {
        const resShapes = Array<IShape>();
        if (!layers?.length) return resShapes;
        const offsetTop = this.calcLayerOffsetTop(layers[0]);

        const getLayersShape = (layers: ILayerProps[], deep: boolean) => {
            function convertToShape(layer: ILayerProps) {
                const { id, left, top, width, height, rotate } = layer;
                const res: IShape = {
                    id,
                    left,
                    top: top + offsetTop,
                    width,
                    height,
                    rotate,
                };
                return res;
            }
            for (let i = layers.length - 1; i >= 0; i--) {
                const layer = layers[i];
                if (deep) {
                    // 普通点击选中时，需要排除点击到组
                    if (!this.service.flatLayer.checkIsGroup(layer)) {
                        resShapes.push(convertToShape(layer));
                    }
                    getLayersShape(layer.children || [], deep);
                } else {
                    // command成组时，只检测第一层
                    resShapes.push(convertToShape(layer));
                }
            }
        };

        getLayersShape(layers, deep);

        return resShapes;
    }

    /** 检测最外层 */
    tryPickWrap(mouseRect: IShape, layers: ILayerProps[]) {
        const shapes = this.convertToShapes(layers, false);
        // console.timeEnd('convert');
        // console.time('get');
        const ids = getCollisionIds(this.__calcMouseRectOffsetTop(mouseRect), shapes, true);
        // console.timeEnd('get');
        return ids[0] ?? '';
    }

    /** 深度检测子图层 */
    tryPickChild(mouseRect: IShape, layers: ILayerProps[]) {
        const shapes = this.convertToShapes(layers, true);
        const ids = getCollisionIds(this.__calcMouseRectOffsetTop(mouseRect), shapes, true);
        return ids[0] ?? '';
    }

    /** 检测不包括指定的id */
    tryPickWithoutId(mouseRect: IShape, layers: ILayerProps[], withoutId: string) {
        const shapes = this.convertToShapes(layers, false);
        const ids = getCollisionIds(this.__calcMouseRectOffsetTop(mouseRect), shapes);
        pull(ids, withoutId);
        return ids[0] ?? '';
    }

    /** dom 坐标转换为场景坐标 */
    getScenePoint(point: IPoint | MouseEvent | TouchEvent | PointerEvent | React.MouseEvent) {
        return this.__getPointer(
            {
                x: (point as MouseEvent).clientX || (point as IPoint).x,
                y: (point as MouseEvent).clientY || (point as IPoint).y,
            },
            false,
        );
    }

    /** dom 坐标转换为视口操作坐标 */
    getViewportPoint(point: IPoint | MouseEvent | TouchEvent | PointerEvent | React.MouseEvent) {
        return this.__getPointer(
            {
                x: (point as MouseEvent).clientX || (point as IPoint).x,
                y: (point as MouseEvent).clientY || (point as IPoint).y,
            },
            true,
        );
    }

    private __getPointer(point: IPoint, fromViewport = false) {
        const bounds = this.service.editor.getBoundingClientRect();
        const container = this.service.editor.getContainer();
        const event = {
            target: container,
            clientX: point.x,
            clientY: point.y,
        } as unknown as TPointerEvent;

        let pointer = getPointer(event);
        let boundsWidth = bounds.width || 0;
        let boundsHeight = bounds.height || 0;

        if (!boundsWidth || !boundsHeight) {
            if ('top' in bounds && 'bottom' in bounds) {
                boundsHeight = Math.abs(bounds.top - bounds.bottom);
            }
            if ('right' in bounds && 'left' in bounds) {
                boundsWidth = Math.abs(bounds.right - bounds.left);
            }
        }

        const offset = getElementOffset(container, bounds);

        pointer.x = pointer.x - offset.left;
        pointer.y = pointer.y - offset.top;

        if (!fromViewport) {
            pointer = sendPointToPlane(pointer, undefined, this.service.edgelessCanvas.viewportTransform);
        }

        const cssScale =
            boundsWidth === 0 || boundsHeight === 0
                ? new Point(1, 1)
                : new Point(container.clientWidth / boundsWidth, container.clientHeight / boundsHeight);

        const res = pointer.multiply(cssScale);

        return {
            x: ~~res.x,
            y: ~~res.y,
        };
    }
}
